import { StatusCodes } from "http-status-codes";
import { getErrorPageForErrorCode } from "../config/Config";
import { isCognitoSession } from "../utils/auth";
import { buildLoginRedirectUrl, isSameOriginPath } from "../utils/cognitoAuth";

/**
 * Navigates the browser to the in-app `/login` route for the current Tenant
 * subdomain on a missing/expired session (HTTP 401), carrying the current
 * in-application path as the Original_Destination_URL so the person is returned
 * to it after logging in. This replaces the former
 * `errorPage.unauthorized` rendering for the Cognito login path.
 *
 * `redirectToLogin` is invoked from the non-component `fetch` handler below, so
 * it cannot use React Router's `useNavigate` hook. Now that `/login` is an
 * in-app SPA route on the same origin, `buildLoginRedirectUrl` returns an
 * in-app RELATIVE path and this navigates to it via `window.location.assign` —
 * a same-origin navigation within the Tenant's own subdomain, not the former
 * cross-origin full-page redirect. A same-origin `location.assign` is preferred
 * here over reusing the `service-error` CustomEvent (which drives the generic
 * error-page `<Navigate>` in App.js) or introducing a router/history singleton,
 * keeping this handler self-contained and its navigation target explicit.
 *
 * This redirect is reserved for Cognito-established sessions. In-session API
 * calls from a Canvas/LTI-launched user DO flow through this same request
 * helper, so an expired LTI cookie can also produce a 401 here; those are
 * deliberately NOT sent to `/login` (see the 401 branch below, which gates
 * `redirectToLogin` on `isCognitoSession()` and otherwise preserves the prior
 * `errorPage.unauthorized` behavior). A Canvas/LTI user re-establishes a session
 * via a fresh LTI launch, not via this in-app login page.
 *
 * The current path is validated with `isSameOriginPath` before it is carried
 * through; a value that is not a same-origin relative path is discarded and the
 * navigation proceeds with no Original_Destination_URL.
 *
 * If the browser is ALREADY on `/login`, this is a no-op. Otherwise a 401 from
 * an API call made while on the login page (e.g. a stale/expired `ddl` cookie
 * still present in the browser makes the app's cookie-presence auth check read
 * "authenticated", so an App-level data-load fires and 401s) would redirect
 * `/login` -> `/login`, each hop folding the previous `/login?originalDestination=...`
 * into a fresh `originalDestination` and reloading — an infinite loop with an
 * ever-growing, repeatedly-encoded URL. Guarding here (rather than only fixing
 * whatever issued the call) stops the loop at its return edge regardless of the
 * trigger. Incognito never hit this because it has no stale cookie, so the
 * cookie-presence check is false and the App data-load never runs.
 */
const LOGIN_PATH = "/login";

function redirectToLogin() {
  // Already on the login page — do not redirect it to itself (see above).
  if (window.location.pathname === LOGIN_PATH) {
    return;
  }
  const currentPath = window.location.pathname + window.location.search;
  const originalDestination = isSameOriginPath(currentPath) ? currentPath : "";
  window.location.assign(buildLoginRedirectUrl(originalDestination));
}

function request(
  url,
  params = {},
  method,
  withCredentials = true,
  ignoreRedirect = false,
  ignoreJson = false,
  signal = null,
  retryCount = 1,
  body = {}
) {
  const options = { method: method };
  let urlWithParams = url;
  if (signal) {
    options.signal = signal;
  }
  if (withCredentials) {
    options.credentials = "include";
  }
  if (!!params && Object.keys(params).length > 0) {
    urlWithParams += "?" + new URLSearchParams(params).toString();
  }
  if (method !== "GET") {
    if (body instanceof FormData) {
      options.body = body;
    } else if (!!body && Object.keys(body).length > 0) {
      options.body = JSON.stringify(body);
    }
  }

  return fetch(urlWithParams, options)
    .then((res) => {
      if (res.ok) {
        return ignoreJson ? res : res.json();
      }
      return Promise.reject(res);
    })
    .catch((res) => {
      if (window["ddlDebug"]) {
        console.error("Fetch error: ", res);
      }
      const status =
        res instanceof Response ? res.status : StatusCodes.BAD_GATEWAY;
      if (
        status === StatusCodes.UNAUTHORIZED ||
        status === StatusCodes.NOT_FOUND
      ) {
        if (ignoreRedirect) {
          return Promise.reject(res);
        }
        // A 401 means the Session_Cookie is missing or expired. The response
        // depends on how the session was established:
        //  - Cognito login path (`isCognitoSession()` true): redirect to the
        //    Amplify_Login_UI `/login` page so the person can re-authenticate.
        //  - Canvas/LTI path (marker absent): keep the pre-existing behavior and
        //    fall through to the `service-error` dispatch that renders
        //    `errorPage.unauthorized`. Those users have no Insights `/login` page
        //    to return to; they re-establish a session via a fresh Canvas/LTI
        //    launch, so sending them to `/login` would strand them.
        if (status === StatusCodes.UNAUTHORIZED && isCognitoSession()) {
          redirectToLogin();
          return;
        }
        const errorPage = getErrorPageForErrorCode(status);
        if (!window.location.href.endsWith(errorPage)) {
          window.dispatchEvent(
            new CustomEvent("service-error", { detail: { errorPage } })
          );
        }
        return;
      }
      // Retry ONCE on 504 Gateway Timeout for GET requests
      if (
        (status === StatusCodes.GATEWAY_TIMEOUT ||
          !(res instanceof Response)) &&
        method === "GET" &&
        retryCount > 0
      ) {
        return request(
          url,
          params,
          method,
          withCredentials,
          ignoreRedirect,
          ignoreJson,
          signal,
          retryCount - 1
        );
      }
      return Promise.reject(res);
    });
}

export function getRequest(url, params, ignoreRedirect = false, signal = null) {
  return request(url, params, "GET", true, ignoreRedirect, false, signal);
}

export function postRequest(
  url,
  body,
  withCredentials = true,
  ignoreRedirect = false,
  ignoreJson = false,
  signal = null,
  params
) {
  return request(
    url,
    params,
    "POST",
    withCredentials,
    ignoreRedirect,
    ignoreJson,
    signal,
    1,
    body
  );
}

export function putRequest(
  url,
  body,
  withCredentials = true,
  ignoreRedirect = false,
  ignoreJson = false,
  signal = null,
  params
) {
  return request(
    url,
    params,
    "PUT",
    withCredentials,
    ignoreRedirect,
    ignoreJson,
    signal,
    1,
    body
  );
}

export function patchRequest(
  url,
  body,
  withCredentials = true,
  ignoreRedirect = false,
  ignoreJson = false,
  signal = null,
  params
) {
  return request(
    url,
    params,
    "PATCH",
    withCredentials,
    ignoreRedirect,
    ignoreJson,
    signal,
    1,
    body
  );
}

export function deleteRequest(
  url,
  body,
  withCredentials = true,
  ignoreRedirect = false,
  ignoreJson = false,
  signal = null,
  params
) {
  return request(
    url,
    params,
    "DELETE",
    withCredentials,
    ignoreRedirect,
    ignoreJson,
    signal,
    1,
    body
  );
}
